import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { memberColors } from '../../../styles/tokens'
import { Portal } from '../../../components/Portal'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

async function callAdminUsers(action, extra = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-users`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey':        SUPABASE_KEY,
    },
    body: JSON.stringify({ action, ...extra }),
  })
  const body = await res.json()
  if (!res.ok) throw new Error(body.error ?? 'Unknown error')
  return body
}

async function callAdminStripe(action, extra = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-stripe`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey':        SUPABASE_KEY,
    },
    body: JSON.stringify({ action, ...extra }),
  })
  const body = await res.json()
  if (!res.ok) throw new Error(body.error ?? 'Unknown error')
  return body
}

const Section = ({ title, children }) => (
  <div className="bg-surface rounded-base border border-border overflow-hidden">
    <div className="px-5 py-3 bg-background border-b border-border">
      <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
)

const Field = ({ label, value, className }) => (
  <div>
    <p className="text-xs font-medium text-text-muted mb-0.5">{label}</p>
    <p className={cn('text-sm text-text-primary', className)}>{value ?? '—'}</p>
  </div>
)

export const UserDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [notes,           setNotes]         = useState(null)
  const [compCycle,       setCompCycle]      = useState('monthly')
  const [showGrant,       setShowGrant]      = useState(false)
  const [showRevoke,      setShowRevoke]     = useState(false)
  const [showSuspend,     setShowSuspend]    = useState(false)
  const [showDelete,      setShowDelete]     = useState(false)
  const [deleteConfirm,   setDeleteConfirm]  = useState('')
  const [actionError,     setActionError]    = useState('')
  const [actionLoading,   setActionLoading]  = useState(null)

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          subscriptions!subscriptions_user_id_profiles_fkey(*),
          household_members!household_members_user_id_profiles_fkey(role, households(id, name))
        `)
        .eq('id', id)
        .single()
      if (error) throw error
      if (notes === null) setNotes(data.internal_notes ?? '')
      return data
    },
    enabled: !!id,
  })

  const { data: billing } = useQuery({
    queryKey: ['admin', 'billing-history', id],
    queryFn: () => callAdminStripe('get_billing_history', { user_id: id }),
    enabled: !!id,
  })

  const sub = profileData?.subscriptions?.[0]

  const doAction = async (action, extra = {}) => {
    setActionError('')
    setActionLoading(action)
    try {
      await callAdminUsers(action, { target_user_id: id, ...extra })
      await qc.refetchQueries({ queryKey: ['admin', 'user', id] })
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      setShowGrant(false); setShowRevoke(false)
      setShowSuspend(false); setShowDelete(false)
    } catch (err) {
      setActionError(err.message)
    }
    setActionLoading(null)
  }

  const saveNotes = async () => {
    await supabase.from('profiles').update({ internal_notes: notes }).eq('id', id)
  }

  if (isLoading) return (
    <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>
  )

  if (!profileData) return (
    <div className="px-8 py-8"><p className="text-text-muted">User not found.</p></div>
  )

  const color = memberColors[profileData.member_color] ?? memberColors.violet
  const initials = (profileData.display_name ?? profileData.email ?? '?')
    .split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div className="px-8 py-8 max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/users')} className="text-text-muted hover:text-primary">
          ← Back
        </button>
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
          style={{ background: color }}
        >
          {initials}
        </div>
        <div>
          <h1 className="text-xl font-bold text-text-primary">{profileData.display_name ?? '(no name)'}</h1>
          <p className="text-sm text-text-muted">{profileData.email}</p>
        </div>
        {profileData.is_suspended && (
          <span className="ml-2 px-2 py-1 text-xs font-bold bg-rose-100 text-rose-700 rounded-full">Suspended</span>
        )}
      </div>

      {actionError && (
        <div className="rounded-base bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
          {actionError}
        </div>
      )}

      {/* Profile */}
      <Section title="Profile">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="User ID"    value={<span className="font-mono text-xs">{profileData.id}</span>} />
          <Field label="Email"      value={profileData.email} />
          <Field label="Display name" value={profileData.display_name} />
          <Field label="Member color" value={
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full inline-block" style={{ background: color }} />
              {profileData.member_color}
            </span>
          } />
          <Field label="Joined" value={profileData.created_at ? new Date(profileData.created_at).toLocaleDateString() : '—'} />
          <Field label="Super admin" value={profileData.is_super_admin ? 'Yes' : 'No'} />
        </div>
      </Section>

      {/* Subscription */}
      <Section title="Subscription">
        {sub ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Plan"    value={sub.plan} />
            <Field label="Status"  value={sub.status} />
            <Field label="Billing cycle" value={sub.billing_cycle ?? '—'} />
            <Field label="Period end" value={sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—'} />
            <Field label="Stripe customer" value={
              sub.stripe_customer_id
                ? <a href={`https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`}
                    target="_blank" rel="noreferrer"
                    className="text-primary hover:underline font-mono text-xs">
                    {sub.stripe_customer_id}
                  </a>
                : '—'
            } />
            <Field label="Comped" value={sub.is_comped ? 'Yes' : 'No'} />
          </div>
        ) : (
          <p className="text-sm text-text-muted">No subscription record.</p>
        )}
      </Section>

      {/* Comp controls */}
      <Section title="Comp Access">
        <div className="flex items-center gap-4 flex-wrap">
          {!sub?.is_comped ? (
            <>
              <Button size="sm" onClick={() => setShowGrant(true)}>
                Grant Premium
              </Button>
              <p className="text-xs text-text-muted">No Stripe charge — marks account as comped.</p>
            </>
          ) : (
            <>
              <div className="space-y-1 flex-1">
                <p className="text-xs text-text-muted">
                  Comped at: {sub.comped_at ? new Date(sub.comped_at).toLocaleString() : '—'}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setShowRevoke(true)}>
                Revoke Premium
              </Button>
            </>
          )}
        </div>
      </Section>

      {/* Households */}
      <Section title="Households">
        {(profileData.household_members ?? []).length === 0 ? (
          <p className="text-sm text-text-muted">No households.</p>
        ) : (
          <div className="space-y-2">
            {profileData.household_members.map((m) => (
              <div key={m.households?.id} className="flex items-center justify-between text-sm">
                <span className="font-medium text-text-primary">{m.households?.name ?? '?'}</span>
                <span className="text-xs capitalize text-text-muted bg-background border border-border px-2 py-0.5 rounded-full">{m.role}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Internal notes */}
      <Section title="Internal Notes">
        <textarea
          className="w-full rounded-base border border-border bg-background px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          rows={4}
          placeholder="Notes visible only to DoorChart staff…"
          value={notes ?? ''}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNotes}
        />
        <p className="text-xs text-text-muted mt-1">Auto-saves on blur.</p>
      </Section>

      {/* Billing history */}
      <Section title="Billing History (last 10)">
        {!billing ? (
          <Spinner size="sm" />
        ) : billing.charges?.length === 0 ? (
          <p className="text-sm text-text-muted">No charges found.</p>
        ) : (
          <div className="space-y-2">
            {billing.charges?.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                <div>
                  <span className="font-medium text-text-primary">
                    ${(c.amount / 100).toFixed(2)} {c.currency?.toUpperCase()}
                  </span>
                  <span className="ml-2 text-text-muted">{c.description ?? 'Charge'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={cn(
                    'text-[11px] px-2 py-0.5 rounded-full font-semibold',
                    c.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                  )}>
                    {c.status}
                  </span>
                  <span className="text-xs text-text-muted">{new Date(c.created * 1000).toLocaleDateString()}</span>
                  {c.receipt_url && (
                    <a href={c.receipt_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                      Receipt
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Actions */}
      <Section title="Actions">
        <div className="flex flex-wrap gap-3">
          <Button size="sm" variant="secondary" onClick={() => doAction('reset_password')} disabled={!!actionLoading}>
            {actionLoading === 'reset_password' ? <Spinner size="sm" /> : 'Send Password Reset'}
          </Button>
          {profileData.is_suspended ? (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600" onClick={() => setShowSuspend(true)} disabled={!!actionLoading}>
              Unsuspend Account
            </Button>
          ) : (
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white border-amber-500" onClick={() => setShowSuspend(true)} disabled={!!actionLoading}>
              Suspend Account
            </Button>
          )}
          <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white border-rose-600" onClick={() => { setShowDelete(true); setDeleteConfirm('') }} disabled={!!actionLoading}>
            Delete Account
          </Button>
        </div>
      </Section>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}

      {showGrant && (
        <Modal title="Grant Premium" onClose={() => setShowGrant(false)}>
          <p className="text-sm text-text-secondary mb-4">
            This will set the account to <strong>Paid</strong> with no Stripe charge.
          </p>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-text-primary">Billing cycle (for display)</label>
            <select
              value={compCycle}
              onChange={(e) => setCompCycle(e.target.value)}
              className="w-full rounded-base border border-border bg-surface px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="monthly">Monthly ($4/mo display)</option>
              <option value="annual">Annual ($40/yr display)</option>
              <option value="lifetime">Lifetime ($120 display)</option>
            </select>
          </div>
          <div className="flex gap-2 mt-5 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowGrant(false)}>Cancel</Button>
            <Button size="sm" onClick={() => doAction('grant_comp', { billing_cycle: compCycle })} disabled={actionLoading === 'grant_comp'}>
              {actionLoading === 'grant_comp' ? <Spinner size="sm" /> : 'Grant Premium'}
            </Button>
          </div>
        </Modal>
      )}

      {showRevoke && (
        <Modal title="Revoke Premium" onClose={() => setShowRevoke(false)}>
          <p className="text-sm text-text-secondary mb-5">
            This will remove comp status and set the account back to Free. The user will lose access to premium features immediately.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowRevoke(false)}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white border-rose-600" onClick={() => doAction('revoke_comp')} disabled={actionLoading === 'revoke_comp'}>
              {actionLoading === 'revoke_comp' ? <Spinner size="sm" /> : 'Revoke Premium'}
            </Button>
          </div>
        </Modal>
      )}

      {showSuspend && (
        <Modal
          title={profileData.is_suspended ? 'Unsuspend Account' : 'Suspend Account'}
          onClose={() => setShowSuspend(false)}
        >
          <p className="text-sm text-text-secondary mb-5">
            {profileData.is_suspended
              ? 'Restore access for this user and re-enable their account.'
              : 'This will immediately block all database access and invalidate all active sessions.'}
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowSuspend(false)}>Cancel</Button>
            <Button
              size="sm"
              className={profileData.is_suspended
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600'
                : 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500'}
              onClick={() => doAction(profileData.is_suspended ? 'unsuspend' : 'suspend')}
              disabled={!!actionLoading}
            >
              {actionLoading === 'suspend' || actionLoading === 'unsuspend' ? <Spinner size="sm" /> : (profileData.is_suspended ? 'Unsuspend' : 'Suspend')}
            </Button>
          </div>
        </Modal>
      )}

      {showDelete && (
        <Modal title="Delete Account" onClose={() => setShowDelete(false)}>
          <p className="text-sm text-text-secondary mb-4">
            This is <strong>permanent and irreversible</strong>. All data will be deleted. If this user is the sole admin of any household, that household will also be deleted.
          </p>
          <p className="text-sm text-text-secondary mb-3">
            Type <code className="bg-background px-1 py-0.5 rounded text-rose-600 font-mono">{profileData.email}</code> to confirm.
          </p>
          <input
            type="text"
            placeholder={profileData.email}
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
          />
          <div className="flex gap-2 mt-4 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white border-rose-600"
              disabled={deleteConfirm !== profileData.email || actionLoading === 'delete_account'}
              onClick={() => doAction('delete_account')}
            >
              {actionLoading === 'delete_account' ? <Spinner size="sm" /> : 'Permanently Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

const Modal = ({ title, children, onClose }) => (
  <Portal>
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
    <div className="bg-surface rounded-base shadow-card w-full max-w-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-text-primary">{title}</h3>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {children}
    </div>
  </div>
  </Portal>
)
